import * as spacesApi from "@/api/spaces";
import { getFrameDefaultSize } from "@/lib/frame-defaults";
import { useCanvasStore } from "@/stores/canvas-store";
import { useFramesStore } from "@/stores/frames-store";
import { selectFramesByType } from "@/stores/frames-selectors";
import { useNotificationStore } from "@/stores/notification-store";
import { translate } from "@/i18n";
import { canvasMessages } from "@/i18n/messages/canvas";
import { queueTerminalFrameAutostart } from "@/components/shapes/terminal-frame/autostart";
import { ingestFrame } from "@/lib/frame-ingest";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";

const TERMINAL_SIZE = getFrameDefaultSize("terminal");

interface CreateTerminalFrameOptions {
  alias?: string;
  command?: string;
  cwd?: string;
  sessionId?: string;
}

export async function createTerminalFrame(
  projectId: string,
  spaceId: string,
  options: CreateTerminalFrameOptions = {},
) {
  const { selectFrame } = useCanvasStore.getState();
  const terminalCount = selectFramesByType(
    useFramesStore.getState(),
    "terminal",
  ).length;
  const alias = options.alias ?? `terminal-${terminalCount + 1}`;

  try {
    const dirs = await spacesApi.listProjectDirectories(projectId);
    const cwd =
      options.cwd ?? dirs.find((dir) => dir.is_repo)?.path ?? dirs[0]?.path;
    const frame = await spacesApi.createFrame(projectId, spaceId, {
      frame_type: "terminal",
      title: alias,
      content: {
        alias,
        cwd,
        command: options.command ?? "/bin/zsh",
        autostartSessionId: options.sessionId,
        lastStatus: "stopped",
      },
      width: TERMINAL_SIZE.w,
      height: TERMINAL_SIZE.h,
    });
    queueTerminalFrameAutostart(frame.id);
    ingestFrame(frame);
    selectFrame(frame.id);
    workspaceViewAdapter().revealFrames([frame.id]);
    return frame;
  } catch (err) {
    const msg =
      err instanceof Error
        ? err.message
        : translate(canvasMessages, "unknownError");
    useNotificationStore.getState().addSystemToast({
      id: `terminal-create-fail-${Date.now()}`,
      level: "error",
      title: translate(canvasMessages, "createTerminalFailed"),
      description: msg,
    });
    return null;
  }
}
